import { AddExtraModal, NewExtra } from "@/src/app/Bookings/Tabs/EquipmentAndExtras";
import { FormField, FormItem, FormControl, FormLabel } from "@/src/components/ui/form";
import { Input } from "@/src/components/ui/input";
import { ChevronDown, InfoIcon, Plus } from "lucide-react";
import { OfferYachtForm } from "../offer-yacht.schema";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/src/components/ui/tooltip";
import { RadioGroup, RadioGroupItem } from "@/src/components/ui/radio-group";
import { Label } from "@/src/components/ui/label";
import { Separator } from "@/src/components/ui/separator";
import { Button } from "@/src/components/ui/button";
import { Checkbox } from "@/src/components/ui/checkbox";
import { Amenity, YachtDetails } from "@/src/app/Yachts/model";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useOffersStore } from "@/src/stores/offersStore";
import { calculateDaysStarted, calculateWeeksStarted, YachtPrice } from "./ChangeYachtDetailsModal";
import { calculateCurrencyPrice } from "@/src/lib/utils";
import { useFormContext } from "react-hook-form";
import { useSubscribe } from "@/src/lib/actions";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/src/components/ui/collapsible";
import { AmenityConfig } from "./useExtras";
import { Currency } from "@/src/app/Currencies/model";
import { logOfferYachtControlChange, OfferYachtLogContext, useOfferCreatorBlurLogger } from "../../offerCreatorLogs";

export type ExtraServicesProps = {
  yacht: YachtDetails;
  amenities: Amenity[];
  amenitiesDetails: AmenityConfig[];
  price: YachtPrice | null;
  offerCurrencyCode: string;
  index: number;
  logContext: OfferYachtLogContext;
};
export function ExtraServices({
  amenities,
  amenitiesDetails,
  offerCurrencyCode,
  price,
  yacht,
  index,
  logContext,
}: ExtraServicesProps) {
  const form = useFormContext<OfferYachtForm>();
  const { captureFieldFocus, logOfferYachtControlBlur } = useOfferCreatorBlurLogger();
  const matchingAmenity: (am: any) => Amenity = (am) => {
    const res = amenities.find((a) => a.id === am.id)!;
    return res ?? emptyAmenity();
  };
  const amenityCurrency: (am: any) => Currency = (am) => {
    return (matchingAmenity(am).currency as Currency | undefined) ?? yachtCurrency!;
  };
  const { currencies } = useOffersStore();

  const customAmenityCurrency = (currId: string) => {
    const curr = currencies.filter((curr) => curr.id === currId);
    return curr;
  };
  const [addAmenityModalOpen, setAddAmenityModalOpen] = useState(false);

  const yachtCurrencyCode = price?.currency || "EUR";
  const yachtCurrency = currencies?.find((c) => c.code === yachtCurrencyCode);
  const offerCurrency = currencies?.find((c) => c.code === (offerCurrencyCode ?? "EUR"));
  const yachtLocalAmenitiesCurrencyCode = useMemo(
    () =>
      amenities
        .filter((am) => am.paid_locally)
        .map((am) => am.currency?.code)
        .filter((v, i, a) => a.indexOf(v) === i)
        .map((x) => {
          return x;
        })[0] ?? currencies?.find((c) => c.code === (price?.currency || "EUR"))!.code,
    [amenities, currencies, price],
  );

  const calculateBasedPrice = (amount?: number) => {
    if (!amount) return 0;
    return calculateCurrencyPrice(yachtCurrency!, offerCurrency!, amount ?? 0);
  };

  const calculateOfferPrice = (amount: number, currency = offerCurrency?.code) => {
    return new Intl.NumberFormat("en-EN", { style: "currency", currency }).format(amount);
  };

  const calculateCurrency = (amount?: number) => {
    if (!amount) return 0;
    return calculateCurrencyPrice(offerCurrency!, yachtCurrency!, amount ?? 0);
  };

  const defaultAmenities = amenitiesDetails.filter((am) => !am.isCustomAmenity && !am.included);
  const initialCustomAmenities = amenitiesDetails.filter((am) => am.isCustomAmenity);
  const customAmenities = form.watch("customAmenities");

  const startDate = form.watch("startDate");
  const endDate = form.watch("endDate");

  const onNewExtraAdded = (data: NewExtra) => {
    let days = 1,
      weeks = 1;
    if (startDate && endDate) {
      days = calculateDaysStarted(startDate, endDate);
      weeks = calculateWeeksStarted(startDate, endDate);
    }
    const mult = ["per day", "per night"].includes(data.per) ? days : ["per week"].includes(data.per) ? weeks : 1;
    const currencyId = currencies.find((c) => c.code === data.currency)?.id;
    const newCustomAmenity = {
      ...data,
      checked: true,
      comment: "",
      currencyCode: data.currency!,
      currencyId: currencyId!,
      dedupeKey: "",
      included: data.price === 0 && data.freeOfCharge !== true,
      isCustomAmenity: true,
      isPercentage: false,
      manuallyAssigned: false,
      number: 1,
      paid: data.price !== 0,
      percentageType: "",
      yachtId: yacht?.id!,
      finalBookingPrice: data.price * mult,
      price: data.price * mult,
      yachtIndex: index,
      per: data.per,
    };

    form.setValue("customAmenities", [
      ...customAmenities,
      newCustomAmenity,
    ]);
    logExtraChange("customAmenities.added", null, newCustomAmenity, newCustomAmenity, "custom");
    setAddAmenityModalOpen(false);
  };

  useSubscribe("booking:new-extra", onNewExtraAdded);

  const amenitiesArray = amenitiesDetails;

  const updateAmenitiesDetails = (index: number, value: any) => {
    form.setValue(`amenitiesDetails.${index}`, value);
  };

  const logExtraChange = (
    field: string,
    before: unknown,
    after: unknown,
    extra: any,
    extraType: "standard" | "custom",
  ) => {
    logOfferYachtControlChange({
      eventType: "offer_yacht.extra_change",
      context: logContext,
      field,
      before,
      after,
      meta: {
        extra_type: extraType,
        extra_id: extra?.id ?? extra?.dedupeKey ?? null,
        extra_text: extra?.text ?? null,
      },
    });
  };

  const customAmenityCurrencySymbol = (am: OfferYachtForm["customAmenities"][number]) =>
    currencies.find((c) => c.code === am.currencyCode)?.symbol || "";

  const initialized = useRef(false);
  useEffect(() => {
    if (!initialized.current && (!customAmenities || customAmenities.length === 0)) {
      form.setValue("customAmenities", initialCustomAmenities as any);
      initialized.current = true;
    }
  }, [customAmenities, initialCustomAmenities]);

  return (
    <>
      {amenities.length > 0 && (
        <div>
          <h4 className="text-xl font-semibold mb-6">Extra services</h4>
          <div className="flex flex-col mb-6">
            {defaultAmenities.map((am, i) => {
              return (
                <Fragment key={am.id}>
                  <Collapsible>
                    <div className="flex flex-row items-center gap-2">
                      <Checkbox
                        className="w-6 h-6"
                        checked={am.checked}
                        onCheckedChange={(checked) => {
                          const next = {
                            ...am,
                            checked: !!checked,
                          };
                          logExtraChange("checked", am.checked, next.checked, next, "standard");
                          updateAmenitiesDetails(i, next);
                        }}
                      />
                      <CollapsibleTrigger className="flex flex-row items-center gap-2 py-6 justify-between w-full text-left transition-transform duration-300 data-[state=open]:rotate-0 data-[state=open]:text-blue-600">
                        <span className="text-gray-800">{am.text}</span>
                        <ChevronDown className="h-5 w-5 text-gray-500 transition-transform duration-300 data-[state=open]:rotate-180" />
                      </CollapsibleTrigger>
                    </div>
                    <CollapsibleContent className="flex flex-col gap-6 data-[state=open]:mt-6">
                      <div className="pl-8 flex gap-10">
                        <RadioGroup
                          className="flex flex-col justify-start gap-3 items-start"
                          defaultValue={am.paidLocally ? "locally" : "advance"}
                          onValueChange={(value) => {
                            const amCurrency = amenityCurrency(am);
                            const next = {
                              ...am,
                              paidLocally: value === "locally",
                              price:
                                value === "locally"
                                  ? calculateCurrencyPrice(amCurrency!, offerCurrency!, am.price)
                                  : calculateCurrencyPrice(offerCurrency!, amCurrency!, am.price),
                              currency: value === "locally" ? amCurrency.code! : offerCurrencyCode,
                            };
                            logExtraChange("paidLocally", am.paidLocally, next.paidLocally, next, "standard");
                            updateAmenitiesDetails(i, next);
                          }}
                        >
                          <span className="text-xs">Paid</span>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="locally" id={am.id + "1paid"} />
                            <Label htmlFor={am.id + "1paid"}>Paid locally</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="advance" id={am.id + "2paid"} />
                            <Label htmlFor={am.id + "2paid"}>Paid in advance</Label>
                          </div>
                        </RadioGroup>
                        <RadioGroup
                          className="flex flex-col justify-start gap-3 items-start"
                          defaultValue={am.obligatory ? "obligatory" : "nonobligatory"}
                          onValueChange={(value) => {
                            const next = {
                              ...am,
                              obligatory: value === "obligatory",
                            };
                            logExtraChange("obligatory", am.obligatory, next.obligatory, next, "standard");
                            updateAmenitiesDetails(i, next);
                          }}
                        >
                          <span className="text-xs">Obligatory</span>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="obligatory" id={am.id + "1obligatory"} />
                            <Label htmlFor={am.id + "1obligatory"}>Obligatory</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="nonobligatory" id={am.id + "2obligatory"} />
                            <Label htmlFor={am.id + "2obligatory"}>Nonobligatory</Label>
                          </div>
                        </RadioGroup>
                        <RadioGroup
                          className="flex flex-col justify-start gap-3 items-start"
                          defaultValue={am.hidden ? "hidden" : "visible"}
                          onValueChange={(value) => {
                            const next = {
                              ...am,
                              hidden: value == "hidden",
                            };
                            logExtraChange("hidden", am.hidden, next.hidden, next, "standard");
                            updateAmenitiesDetails(i, next);
                          }}
                        >
                          <span className="text-xs">Visibility</span>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="hidden" id={am.id + "1hidden"} />
                            <Label htmlFor={am.id + "1hidden"}>Hidden</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="visible" id={am.id + "2hidden"} />
                            <Label htmlFor={am.id + "2hidden"}>Visible</Label>
                          </div>
                        </RadioGroup>
                        <RadioGroup
                          className="flex flex-col justify-start gap-3 items-start"
                          defaultValue={am.freeOfCharge ? "yes" : "no"}
                          onValueChange={(value) => {
                            const next = {
                              ...am,
                              freeOfCharge: value === "yes",
                              price: value === "yes" ? 0 : am.price,
                              finalBookingPrice: value === "yes" ? 0 : am.price,
                            };
                            logExtraChange("freeOfCharge", am.freeOfCharge, next.freeOfCharge, next, "standard");
                            updateAmenitiesDetails(i, next);
                          }}
                        >
                          <span className="text-xs">Free of Charge</span>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="yes" id={am.id + "yes"} />
                            <Label htmlFor={am.id + "yes"}>Yes</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="no" id={am.id + "no"} />
                            <Label htmlFor={am.id + "no"}>No</Label>
                          </div>
                        </RadioGroup>
                      </div>
                      <div>
                        <span className="pl-8 text-xs pt-2">Final price for booking</span>
                        <div className="relative ml-8 flex gap-2 items-center">
                          <span className="absolute right-[calc(100%+8px)] top-1/2 -translate-y-1/2">
                            {am.paidLocally ? amenityCurrency(am)?.symbol : offerCurrency?.symbol}
                          </span>
                          <Input
                            className="w-20"
                            value={am.price}
                            onFocus={() => captureFieldFocus(`amenitiesDetails.${i}.price`, am.price)}
                            onChange={(val) => {
                              const next = {
                                ...am,
                                price: +val.currentTarget.value,
                              };
                              updateAmenitiesDetails(i, next);
                            }}
                            onBlur={() => {
                              const next = form.getValues("amenitiesDetails")?.[i] as any;
                              logOfferYachtControlBlur({
                                eventType: "offer_yacht.extra_change",
                                context: logContext,
                                key: `amenitiesDetails.${i}.price`,
                                field: "price",
                                before: am.price,
                                after: next?.price,
                                meta: {
                                  extra_type: "standard",
                                  extra_id: next?.id ?? next?.dedupeKey ?? null,
                                  extra_text: next?.text ?? null,
                                },
                              });
                            }}
                          />
                          <Tooltip>
                            <TooltipTrigger>
                              <InfoIcon size={16} />
                            </TooltipTrigger>
                            <TooltipContent align="start">
                              <div className="max-w-[600px] whitespace-pre">{am?.formula}</div>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                      </div>

                      <div className="rounded-lg bg-slate-100 flex flex-col gap-2 p-4 max-w-full">
                        <span>Comment</span>
                        <div
                          className="whitespace-break-spaces flex flex-col"
                          dangerouslySetInnerHTML={{
                            __html: am.description ?? "",
                          }}
                        />
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                  <Separator />
                </Fragment>
              );
            })}
          </div>
        </div>
      )}
      <div>
        <div className={`flex items-center justify-between ${!!customAmenities?.length ? "mb-4" : ""}`}>
          <h4 className="text-md font-semibold">Custom amenities</h4>
          <Button className="gap-1" variant="action" size={"sm"} onClick={() => setAddAmenityModalOpen(true)}>
            Add amenity <Plus size={16} />
          </Button>
        </div>
        <AddExtraModal
          key={`${addAmenityModalOpen}`}
          hideBtn
          index={index}
          localCurrencyCode={yachtLocalAmenitiesCurrencyCode}
          open={addAmenityModalOpen}
          onOpenChange={setAddAmenityModalOpen}
        />
        <FormField<OfferYachtForm, "customAmenities">
          name="customAmenities"
          render={({ field }) => (
            <FormItem className="grid grid-cols-2 gap-x-2">
              {field.value?.map((item, i) => {
                let days = 1,
                  weeks = 1;
                if (startDate && endDate) {
                  days = calculateDaysStarted(startDate, endDate);
                  weeks = calculateWeeksStarted(startDate, endDate);
                }
                const mult = ["per day", "per night"].includes(item.per!)
                  ? days
                  : ["per week"].includes(item.per!)
                    ? weeks
                    : 1;

                return (
                  <FormItem key={item.id || i} className="flex flex-col !my-0 px-4 py-3">
                    <div className="flex flex-row items-start space-x-3 space-y-0">
                      <FormControl>
                        <Checkbox
                          className="w-6 h-6"
                          checked={!!field.value.find((_: any, index) => i === index)?.checked}
                          onCheckedChange={(checked) => {
                            const before = field.value[i];
                            const values = field.value.map((v, index) => {
                              if (i === index) {
                                const hidden = checked ? false : v.hidden || false;
                                return { ...v, checked: checked, hidden };
                              } else {
                                return v;
                              }
                            });
                            logExtraChange("checked", before?.checked, values[i]?.checked, values[i], "custom");
                            field.onChange(values);
                          }}
                        />
                      </FormControl>
                      <FormLabel className="font-normal text-base leading-6">{item.text}</FormLabel>
                    </div>
                    {item.paid && (
                      <span className="text-slate-700 text-sm !mt-1 ml-9">
                        + {calculateOfferPrice(item.price / mult, item.currencyCode)} {item.per}
                      </span>
                    )}
                    <span className="pl-8 text-xs">Paid</span>
                    <RadioGroup
                      className="pl-8 flex flex-row justify-between"
                      defaultValue={field.value[i].paidLocally ? "paid_locally" : "in_advance"}
                      onValueChange={(value) => {
                        const before = field.value[i];
                        const values = field.value.map((am, amenityIdx) => {
                            if (amenityIdx === i) {
                              const paidLocally = value === "paid_locally";
                              const amCurrency = customAmenityCurrency(am.currencyId)[0];
                              const price = paidLocally
                                ? calculateCurrencyPrice(amCurrency!, offerCurrency!, am.finalBookingPrice)
                                : calculateCurrencyPrice(offerCurrency!, amCurrency!, am.finalBookingPrice);
                              const currency = paidLocally ? "EUR" : (offerCurrency?.code ?? "EUR");
                              return {
                                ...am,
                                currency,
                                paidLocally,
                                item,
                                finalBookingPrice: price,
                                price,
                                yachtIndex: index,
                              };
                            } else {
                              return {
                                ...am,
                                yachtIndex: index,
                              };
                            }
                          });
                        logExtraChange("paidLocally", before?.paidLocally, values[i]?.paidLocally, values[i], "custom");
                        field.onChange([...values]);
                      }}
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="paid_locally" id={item.text + "1paid"} />
                        <Label htmlFor={item.text + "1paid"}>Paid locally</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="in_advance" id={item.text + "2paid"} />
                        <Label htmlFor={item.text + "2paid"}>Paid in advance</Label>
                      </div>
                    </RadioGroup>
                    <span className="pl-8 text-xs">Obligatory</span>
                    <RadioGroup
                      className="pl-8 flex flex-row justify-between"
                      defaultValue={!!field.value[i].obligatory ? "obligatory" : "nonobligatory"}
                      onValueChange={(value) => {
                        const before = field.value[i];
                        const values = field.value.map((am, obligatoryIdx) => {
                            if (obligatoryIdx === i) {
                              const obligatory = value === "obligatory" ? true : false;
                              return {
                                ...am,
                                obligatory,
                                yachtIndex: index,
                              };
                            } else {
                              return { ...am, yachtIndex: index };
                            }
                          });
                        logExtraChange("obligatory", before?.obligatory, values[i]?.obligatory, values[i], "custom");
                        field.onChange([...values]);
                      }}
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="obligatory" id={item.text + "1obligatory"} />
                        <Label htmlFor={item.text + "1obligatory"}>Obligatory</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="nonobligatory" id={item.text + "2obligatory"} />
                        <Label htmlFor={item.text + "2obligatory"}>Nonobligatory</Label>
                      </div>
                    </RadioGroup>
                    <span className="pl-8 text-xs">Visibility</span>
                    <RadioGroup
                      className="pl-8 flex flex-row justify-between"
                      defaultValue={!!field.value[i].hidden ? "hidden" : "visible"}
                      value={!!field.value[i].hidden ? "hidden" : "visible"}
                      onValueChange={(value) => {
                        const before = field.value[i];
                        const values = field.value.map((am, visibilityIdx) => {
                            if (visibilityIdx === i) {
                              return {
                                ...am,
                                hidden: value === "hidden" ? true : false,
                                checked: value === "hidden" ? false : am.checked,
                                yachtIndex: index,
                              };
                            } else {
                              return { ...am, yachtIndex: index };
                            }
                          });
                        logExtraChange("hidden", before?.hidden, values[i]?.hidden, values[i], "custom");
                        field.onChange([...values]);
                      }}
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="hidden" id={i + "hidden"} />
                        <Label htmlFor={i + "hidden"}>Hidden</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="visible" id={i + "visible"} />
                        <Label htmlFor={i + "visible"}>Visible</Label>
                      </div>
                    </RadioGroup>
                    <span className="pl-8 text-xs">Free of Charge</span>
                    <RadioGroup
                      className="pl-8 flex flex-row justify-between"
                      defaultValue={!!field.value[i].freeOfCharge ? "yes" : "no"}
                      value={!!field.value[i].freeOfCharge ? "yes" : "no"}
                      onValueChange={(value) => {
                        const before = field.value[i];
                        const values = field.value.map((am, visibilityIdx) => {
                            if (visibilityIdx === i) {
                              return {
                                ...am,
                                yachtIndex: index,
                                freeOfCharge: value === "yes",
                                price: value === "yes" ? 0 : am.price,
                                finalBookingPrice: value === "yes" ? 0 : am.price,
                              };
                            } else {
                              return { ...am, yachtIndex: index };
                            }
                          });
                        logExtraChange("freeOfCharge", before?.freeOfCharge, values[i]?.freeOfCharge, values[i], "custom");
                        field.onChange([...values]);
                      }}
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="yes" id={i + "yes"} />
                        <Label htmlFor={i + "yes"}>Yes</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="no" id={i + "no"} />
                        <Label htmlFor={i + "no"}>No</Label>
                      </div>
                    </RadioGroup>
                    <span className="pl-8 text-xs pt-2">Final price for booking</span>
                    <div className="relative ml-8">
                      <span className="absolute right-[calc(100%+8px)] top-1/2 -translate-y-1/2">
                        {field.value[i].paidLocally
                          ? customAmenityCurrencySymbol(field.value[i])
                          : offerCurrency?.symbol}
                      </span>
                      <Input
                        className="w-20"
                        value={field.value[i].finalBookingPrice}
                        onFocus={() =>
                          captureFieldFocus(`customAmenities.${i}.finalBookingPrice`, field.value[i].finalBookingPrice)
                        }
                        onChange={(val) => {
                          const values = field.value.map((am, finalBookingPriceIdx) => {
                              if (i === finalBookingPriceIdx) {
                                return {
                                  ...am,
                                  finalBookingPrice: +val.currentTarget.value,
                                  price: +val.currentTarget.value,
                                  yachtIndex: index,
                                };
                              } else {
                                return { ...am, yachtIndex: index };
                              }
                            });
                          field.onChange([...values]);
                        }}
                        onBlur={() => {
                          const next = form.getValues("customAmenities")?.[i];
                          logOfferYachtControlBlur({
                            eventType: "offer_yacht.extra_change",
                            context: logContext,
                            key: `customAmenities.${i}.finalBookingPrice`,
                            field: "finalBookingPrice",
                            before: field.value[i]?.finalBookingPrice,
                            after: next?.finalBookingPrice,
                            meta: {
                              extra_type: "custom",
                              extra_id: next?.id ?? next?.dedupeKey ?? null,
                              extra_text: next?.text ?? null,
                            },
                          });
                        }}
                      />
                    </div>
                  </FormItem>
                );
              })}
            </FormItem>
          )}
        />
      </div>
    </>
  );
}

const emptyAmenity = () =>
  ({
    id: "",
    comment: "",
    text: "",
    included: false,
    obligatory: false,
    manuallyAssigned: false,
    isPercentage: false,
    paid: false,
    price: 0,
    percentageType: "",
    per: "week",
    // TODO Change to camel on the BE
    paid_locally: true,
    number: 1,
    isCustomAmenity: false,
    description: "",
    formula: "",
    conditionAvailable: "",
  }) satisfies Amenity;
